import * as Schema from "effect/Schema";

import { IsoDateTime, NonNegativeInt, ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { PreviewTabId } from "./preview.ts";
import { PreviewAutomationFrame } from "./previewAutomation.ts";

export const PREVIEW_CONTROL_LEASE_TTL_MS = 30_000;

export const PreviewControlLeaseId = TrimmedNonEmptyString.check(Schema.isMaxLength(128)).pipe(
  Schema.brand("PreviewControlLeaseId"),
);
export type PreviewControlLeaseId = typeof PreviewControlLeaseId.Type;

export const PreviewControlMode = Schema.Literals([
  "watch",
  "pausing",
  "human_control",
  "returning",
]);
export type PreviewControlMode = typeof PreviewControlMode.Type;

export const PreviewControlStatus = Schema.Struct({
  threadId: ThreadId,
  tabId: PreviewTabId,
  available: Schema.Boolean,
  mode: PreviewControlMode,
  controller: Schema.Literals(["none", "agent", "human"]),
  heldByCurrentSession: Schema.Boolean,
  canTakeControl: Schema.Boolean,
  expiresAt: Schema.NullOr(IsoDateTime),
});
export type PreviewControlStatus = typeof PreviewControlStatus.Type;

export const PreviewControlTarget = Schema.Struct({
  threadId: ThreadId,
  tabId: PreviewTabId,
});
export type PreviewControlTarget = typeof PreviewControlTarget.Type;

export const PreviewControlTakeInput = Schema.Struct({
  ...PreviewControlTarget.fields,
  resumeLeaseId: Schema.optional(PreviewControlLeaseId),
});
export type PreviewControlTakeInput = typeof PreviewControlTakeInput.Type;

export const PreviewControlLeaseInput = Schema.Struct({
  ...PreviewControlTarget.fields,
  leaseId: PreviewControlLeaseId,
});
export type PreviewControlLeaseInput = typeof PreviewControlLeaseInput.Type;

export const PreviewControlLeaseResult = Schema.Struct({
  leaseId: PreviewControlLeaseId,
  status: PreviewControlStatus,
});
export type PreviewControlLeaseResult = typeof PreviewControlLeaseResult.Type;

const Coordinate = Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0));
const ScrollDelta = Schema.Finite.check(
  Schema.isGreaterThanOrEqualTo(-100_000),
  Schema.isLessThanOrEqualTo(100_000),
);

export const PreviewControlAction = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("click"),
    x: Coordinate,
    y: Coordinate,
  }),
  Schema.Struct({
    type: Schema.Literal("text"),
    text: Schema.String.check(Schema.isMaxLength(16_384)),
  }),
  Schema.Struct({
    type: Schema.Literal("key"),
    key: TrimmedNonEmptyString.check(Schema.isMaxLength(64)),
    modifiers: Schema.optional(Schema.Array(Schema.Literals(["Alt", "Control", "Meta", "Shift"]))),
  }),
  Schema.Struct({
    type: Schema.Literal("scroll"),
    deltaX: ScrollDelta,
    deltaY: ScrollDelta,
  }),
  Schema.Struct({
    type: Schema.Literal("navigate"),
    url: TrimmedNonEmptyString.check(Schema.isMaxLength(2_048)),
  }),
]);
export type PreviewControlAction = typeof PreviewControlAction.Type;

export const PreviewControlDispatchInput = Schema.Struct({
  ...PreviewControlLeaseInput.fields,
  action: PreviewControlAction,
});
export type PreviewControlDispatchInput = typeof PreviewControlDispatchInput.Type;

export const PreviewControlFrame = PreviewAutomationFrame;
export type PreviewControlFrame = typeof PreviewControlFrame.Type;

export const PreviewControlStreamEvent = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("status"),
    status: PreviewControlStatus,
  }),
  Schema.Struct({
    type: Schema.Literal("frame"),
    sequence: NonNegativeInt,
    status: PreviewControlStatus,
    frame: PreviewControlFrame,
  }),
  Schema.Struct({
    type: Schema.Literal("unavailable"),
    reason: Schema.Literals(["no_host", "no_tab", "capture_failed"]),
    status: PreviewControlStatus,
  }),
]);
export type PreviewControlStreamEvent = typeof PreviewControlStreamEvent.Type;

const PreviewControlErrorFields = {
  threadId: ThreadId,
  tabId: PreviewTabId,
};

export class PreviewControlBusyError extends Schema.TaggedErrorClass<PreviewControlBusyError>()(
  "PreviewControlBusyError",
  {
    ...PreviewControlErrorFields,
    expiresAt: IsoDateTime,
  },
) {
  override get message(): string {
    return "Another client currently controls this browser.";
  }
}

export class PreviewControlLeaseExpiredError extends Schema.TaggedErrorClass<PreviewControlLeaseExpiredError>()(
  "PreviewControlLeaseExpiredError",
  PreviewControlErrorFields,
) {
  override get message(): string {
    return "Browser control expired. Take control again to continue.";
  }
}

export class PreviewControlLeaseMismatchError extends Schema.TaggedErrorClass<PreviewControlLeaseMismatchError>()(
  "PreviewControlLeaseMismatchError",
  PreviewControlErrorFields,
) {
  override get message(): string {
    return "This client does not hold the browser control lease.";
  }
}

export class PreviewControlUnavailableError extends Schema.TaggedErrorClass<PreviewControlUnavailableError>()(
  "PreviewControlUnavailableError",
  PreviewControlErrorFields,
) {
  override get message(): string {
    return "The project browser host is not available.";
  }
}

export const PreviewControlWatchLimitScope = Schema.Literals(["connection", "environment"]);
export type PreviewControlWatchLimitScope = typeof PreviewControlWatchLimitScope.Type;

export class PreviewControlWatchLimitError extends Schema.TaggedErrorClass<PreviewControlWatchLimitError>()(
  "PreviewControlWatchLimitError",
  {
    ...PreviewControlErrorFields,
    scope: PreviewControlWatchLimitScope,
    limit: Schema.Int.check(Schema.isGreaterThan(0)),
  },
) {
  override get message(): string {
    return `The browser preview watch limit for this ${this.scope} is ${this.limit}.`;
  }
}

export const PreviewControlError = Schema.Union([
  PreviewControlBusyError,
  PreviewControlLeaseExpiredError,
  PreviewControlLeaseMismatchError,
  PreviewControlUnavailableError,
  PreviewControlWatchLimitError,
]);
export type PreviewControlError = typeof PreviewControlError.Type;
